import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile } from 'node:fs/promises'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

const NPX_PACKAGE = '@earendil-works/pi-coding-agent'

/**
 * Launcher for Pi coding agent (`npm install -g @earendil-works/pi-coding-agent`).
 *
 * Interactive TUI is `pi` with no positional message and no `-p/--print`.
 * Interview rules go in `--append-system-prompt` so we keep Pi's default
 * tools intact and never write SYSTEM.md into the workspace.
 */
export class PiLauncher implements ProviderLauncher {
  readonly name = 'pi'
  readonly displayName = 'Pi'
  readonly pricingHint = '🆓 Gratuit (open-source, clé API personnelle)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('pi', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable(NPX_PACKAGE)
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('pi', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt =
      config.systemPromptContent ?? (await readFile(config.systemPromptPath, 'utf-8'))

    const args: string[] = ['--append-system-prompt', systemPrompt, '--no-session']

    if (config.model) {
      args.push('--model', config.model)
    }

    if (config.disallowedTools && config.disallowedTools.length > 0) {
      args.push('--exclude-tools', config.disallowedTools.join(','))
    }

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? [NPX_PACKAGE, ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(({ exitCode, signal }) => {
            cleanup()
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('pi', ['--version'])
      return 'pi'
    } catch {
      return 'npx'
    }
  }
}
